// One loader for Places, URL geocoding and maps, shared with any WP-owned SDK.
// No Google request is made until a caller asks for a library.
type Library = 'places' | 'geocoding' | 'maps' | 'marker';
const libraries = new Map<Library, Promise<unknown>>();
let sdk: Promise<void> | undefined;
const timeoutMs = 15000;

function withTimeout<T>(promise: Promise<T>): Promise<T> {
    return new Promise((resolve, reject) => {
        const timeout = window.setTimeout(() => reject(new Error('Google Maps loading timed out')), timeoutMs);
        promise.then(resolve, reject).finally(() => window.clearTimeout(timeout));
    });
}

function loadSdk(firstLibrary: Library): Promise<void> {
    if (window.google?.maps?.importLibrary) return Promise.resolve();
    if (sdk) return sdk;
    sdk = new Promise<void>((resolve, reject) => {
        const existing = Array.from(document.scripts).find(script =>
            /^https:\/\/maps\.(googleapis|google)\.com\/maps\/api\/js(?:\?|$)/.test(script.src));
        if (existing) {
            // Another WP plugin owns this script. Wait for it; never inject a
            // second SDK or overwrite its callback/options.
            const finish = (error?: Error) => {
                window.clearInterval(interval);
                window.clearTimeout(timeout);
                existing.removeEventListener('error', failed);
                if (error) reject(error);
                else resolve();
            };
            const failed = () => finish(new Error('Google Maps failed to load'));
            const interval = window.setInterval(() => {
                if (window.google?.maps?.importLibrary) {
                    finish();
                }
            }, 50);
            existing.addEventListener('error', failed, {once: true});
            const timeout = window.setTimeout(() => {
                finish(window.google?.maps?.importLibrary ? undefined : new Error('Google Maps loading timed out'));
            }, timeoutMs);
            return;
        }
        const script = document.createElement('script');
        const callback = '__rrmGoogleLoaded';
        window.__rrmGoogleLoaded = () => {
            delete window.__rrmGoogleLoaded;
            resolve();
        };
        script.id = 'rrm-google-sdk';
        script.async = true;
        script.nonce = document.querySelector<HTMLScriptElement>('script[nonce]')?.nonce ?? '';
        script.src = 'https://maps.googleapis.com/maps/api/js?' + new URLSearchParams({
            key: 'AIzaSyDSkGeNmxLDv0l762ULmlLmT1k45h9OEAI',
            v: 'weekly', loading: 'async', callback, libraries: firstLibrary,
        });
        script.onerror = () => {
            script.remove();
            delete window.__rrmGoogleLoaded;
            reject(new Error('Google Maps failed to load'));
        };
        document.head.append(script);
    }).catch(error => {sdk = undefined; throw error;});
    return sdk;
}

export function loadGoogleLibrary(library: Library): Promise<unknown> {
    if (!libraries.has(library)) {
        const request = withTimeout(loadSdk(library)
            .then(() => window.google.maps.importLibrary(library)))
            .catch(error => {libraries.delete(library); throw error;});
        libraries.set(library, request);
    }
    return libraries.get(library)!;
}
